import { hasWritingActivity } from "@context/shared";
import type { Draft, SubmissionReview } from "@context/shared";

/** The live message slots on the student side. */
export interface StudentMessages {
  /**
   * Bottom status bar "Review note" pill. Every active review note lives here
   * (one list item each) — the single home for flag text.
   */
  reviewNotes: string[];
  /** Guidance line under the assignment prompt. */
  overview: string;
  /** Guidance line under the assignment prompt. */
  notice: string;
  /** Guidance line under the assignment prompt. */
  guidance: string;
  /** Live summary, milestone card. */
  milestone: string;
  /** Live summary, review card. */
  review: string;
  /** Live summary, integrity card. */
  integrity: string;
}

const EMPTY: StudentMessages = {
  reviewNotes: [],
  overview: "",
  notice: "",
  guidance: "",
  milestone: "",
  review: "",
  integrity: "",
};

/**
 * Derives every live student-facing message from the current draft and review.
 *
 * Review flags feed exactly one place: the bottom status bar's review-note
 * list. The summary and prompt guidance lines stay neutral (steady text or
 * empty) so a flag can never be duplicated into a second slot.
 *
 * `guidanceOverride` carries an action result — a restore, a delete, a
 * validation refusal — and takes precedence over the derived guidance line.
 * (In the original prototype the same messages were written straight to the
 * DOM and then wiped by the next one-second render tick, so they were never
 * readable; keeping them in state fixes that.)
 */
export function deriveStudentMessages(
  draft: Draft,
  review: SubmissionReview,
  guidanceOverride: string | null = null,
): StudentMessages {
  const messages = derive(draft, review);
  return guidanceOverride ? { ...messages, guidance: guidanceOverride } : messages;
}

function derive(draft: Draft, review: SubmissionReview): StudentMessages {
  if (!draft.trackingActive) {
    return {
      ...EMPTY,
      reviewNotes: ["Review notes will appear here after you begin writing."],
      overview: "Tracking is off until you start writing.",
      notice: "You can paste your assignment prompt or open a saved draft before tracking begins.",
      guidance: "Choose “Start This Assignment” when you are ready to write.",
    };
  }

  if (!hasWritingActivity(draft.metrics)) {
    return { ...EMPTY, reviewNotes: ["Review notes will stay blank until you begin writing."] };
  }

  if (review.flags.length === 0) {
    return {
      ...EMPTY,
      reviewNotes: ["No review notes are active right now."],
      overview: review.steadyMessage,
      milestone: review.steadyMessage,
    };
  }

  return {
    ...EMPTY,
    reviewNotes: [...review.flags],
    notice: "Your instructor will see these notes with your submission.",
    guidance: review.instructorReviewNotice,
    integrity: review.instructorReviewNotice,
  };
}
